import { useState, useMemo } from 'react';
import { UserPlus, X, Loader2, Users, UsersRound, Search, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProjectMembers, useAddProjectMember, useRemoveProjectMember } from '@/hooks/use-projects';
import { useProjectTeams, useAddProjectTeam, useRemoveProjectTeam, useTeams } from '@/hooks/use-teams';
import { useUsers } from '@/hooks/use-users';
import { toast } from '@/components/ui/toaster';
import { getInitials } from '@/lib/utils';
import type { ProjectMember } from '@/types';

type ApiErrorLike = { response?: { data?: { message?: string } } };

interface ProjectMembershipDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectMembershipDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: ProjectMembershipDialogProps) {
  const { data: members = [], isLoading: membersLoading } = useProjectMembers(projectId);
  const { data: projectTeams = [], isLoading: teamsLoading } = useProjectTeams(projectId);
  const { data: allTeams = [] } = useTeams();
  const { data: allUsers = [] } = useUsers();
  
  const addMember = useAddProjectMember();
  const removeMember = useRemoveProjectMember();
  const addTeam = useAddProjectTeam();
  const removeTeam = useRemoveProjectTeam();

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('member');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const memberIds = new Set(members.map((m: ProjectMember) => m.userId));
  const availableUsers = allUsers.filter((u) => !memberIds.has(u.id) && u.isActive);

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return availableUsers;
    const query = userSearchQuery.toLowerCase();
    return availableUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    );
  }, [availableUsers, userSearchQuery]);

  const selectedUser = allUsers.find((u) => u.id === selectedUserId);
  
  const assignedTeamIds = new Set(projectTeams.map((pt) => pt.teamId));
  const availableTeams = allTeams.filter((t) => !assignedTeamIds.has(t.id));

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    try {
      await addMember.mutateAsync({
        projectId,
        userId: selectedUserId,
        role: selectedRole,
      });
      setSelectedUserId('');
      toast({ title: 'Member added' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to add member',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeMember.mutateAsync({ projectId, memberId });
      toast({ title: 'Member removed' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to remove member',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleAddTeam = async () => {
    if (!selectedTeamId) return;

    try {
      await addTeam.mutateAsync({ projectId, teamId: selectedTeamId });
      setSelectedTeamId('');
      toast({ title: 'Team added' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to add team',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveTeam = async (teamId: string) => {
    try {
      await removeTeam.mutateAsync({ projectId, teamId });
      toast({ title: 'Team removed' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to remove team',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Project Access</DialogTitle>
          <DialogDescription>
            Manage who can access {projectName}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="members" className="gap-2">
              <Users className="h-4 w-4" />
              Members
            </TabsTrigger>
            <TabsTrigger value="teams" className="gap-2">
              <UsersRound className="h-4 w-4" />
              Teams
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="space-y-4 mt-4">
            {/* Add member */}
            <div className="flex gap-2">
              <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={userPickerOpen}
                    className="flex-1 justify-between"
                  >
                    {selectedUser ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={selectedUser.avatarUrl || undefined} />
                          <AvatarFallback className="text-[10px]">
                            {getInitials(selectedUser.displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <span>{selectedUser.displayName}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Search users...</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <div className="flex items-center border-b px-3">
                    <Search className="h-4 w-4 shrink-0 opacity-50" />
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="border-0 focus-visible:ring-0 h-10"
                    />
                  </div>
                  <ScrollArea className="max-h-[200px]">
                    {filteredUsers.length === 0 ? (
                      <div className="p-4 text-sm text-center text-muted-foreground">
                        {availableUsers.length === 0
                          ? 'No available users'
                          : 'No users found'}
                      </div>
                    ) : (
                      <div className="p-1">
                        {filteredUsers.map((user) => (
                          <button
                            key={user.id}
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setUserPickerOpen(false);
                              setUserSearchQuery('');
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted text-left"
                          >
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={user.avatarUrl || undefined} />
                              <AvatarFallback className="text-[10px]">
                                {getInitials(user.displayName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{user.displayName}</p>
                              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAddMember} disabled={!selectedUserId || addMember.isPending}>
                {addMember.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              </Button>
            </div>

            {/* Members list */}
            <div className="border rounded-lg">
              <ScrollArea className="h-[300px]">
                {membersLoading ? (
                  <div className="p-4 space-y-2">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : members.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No members yet
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {members.map((member: ProjectMember) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.user?.avatarUrl || undefined} />
                            <AvatarFallback className="text-xs">
                              {getInitials(member.user?.displayName || '')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{member.user?.displayName}</p>
                            <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">{member.role}</Badge>
                          {member.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveMember(member.userId)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="teams" className="space-y-4 mt-4">
            {/* Add team */}
            <div className="flex gap-2">
              <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select team..." />
                </SelectTrigger>
                <SelectContent>
                  {availableTeams.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No available teams
                    </div>
                  ) : (
                    availableTeams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        <div className="flex items-center gap-2">
                          <span>{team.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({team.memberCount} members)
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Button onClick={handleAddTeam} disabled={!selectedTeamId || addTeam.isPending}>
                {addTeam.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              </Button>
            </div>

            {/* Teams list */}
            <div className="border rounded-lg">
              <ScrollArea className="h-[300px]">
                {teamsLoading ? (
                  <div className="p-4 space-y-2">
                    {[1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : projectTeams.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <UsersRound className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No teams assigned</p>
                    <p className="text-xs">Add a team to give all its members access.</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {projectTeams.map((pt) => (
                      <div
                        key={pt.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <UsersRound className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{pt.team.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {pt.team.memberCount} members
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleRemoveTeam(pt.teamId)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
